import React, { useState } from 'react';
import '../App.css';
import { getBookLogoUrl } from '../utils/formatters';
import { isGameFinished } from '../utils/constants';
import H2hTable from './H2hTable';
import TotalsTable from './TotalsTable';
import SpreadsTable from './SpreadsTable';

const FootballDataTable = ({ 
  data, 
  gameTime, 
  gameStatus, 
  gameDate, 
  lastUpdate, 
  game, 
  formatPlayerName, 
  getTeamLogoUrl, 
  getBookLogoUrl, 
  isMobile, 
  selectedMarket, 
  selectedMarketName, 
  bookmakers, 
  isLoading, 
  availableMarkets, 
  onMarketSelection, 
  showTiers, 
  onTierToggle, 
  masterTierFilter, 
  onPlayerClick, 
  onOddsClick, 
  isCollapsed = false, 
  onCollapseToggle 
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { 
    away: 'TBD', 
    home: 'TBD', 
    gameIDmlb: 'loading', 
    sport: 'ncaaf',
    awayTeamFull: '',
    homeTeamFull: '',
    ...game,
    // Ensure team names fallback to abbreviations if full names are missing
    awayTeamFull: game?.awayTeamFull || game?.away || 'TBD',
    homeTeamFull: game?.homeTeamFull || game?.home || 'TBD'
  };
  
  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Calculate pagination for mobile
  const totalPages = Math.ceil(bookmakers.length / booksPerPage);
  const currentBooks = isMobile 
    ? bookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : bookmakers;

  const handlePrevBook = () => {
    setMobileBookIndex(prev => (prev - 1 + totalPages) % totalPages);
  };

  const handleNextBook = () => {
    setMobileBookIndex(prev => (prev + 1) % totalPages);
  };

  // Format game time display
  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return 'TBD';
    if (gameStatus === 'Live') return 'LIVE';
    if (isGameFinished(gameStatus)) return gameStatus.toUpperCase();

    // Check if it's already in 12-hour format
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }

    // Convert 24-hour to 12-hour format
    const [hour, minute] = timeStr.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  // Get day of week for future games (returns null if today)
  const getDayOfWeek = (timeStr, gameStatus) => {
    if (gameStatus === 'Live' || gameStatus === 'In Progress' || gameStatus === 'InProgress' || isGameFinished(gameStatus)) {
      return null;
    }

    // Try multiple date sources - gameTime might just be a time string without date
    const dateSources = [
      timeStr,
      safeGame.startTimeEST,
      safeGame.commence_time,
      safeGame.date,
      safeGame.gameDate
    ].filter(Boolean);

    for (const source of dateSources) {
      try {
        // Safari requires ISO 8601 format, so convert space to 'T' if needed
        let dateStr = source;
        if (typeof dateStr === 'string' && dateStr.includes(' ') && !dateStr.includes('T')) {
          dateStr = dateStr.replace(' ', 'T');
        }
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          // Check if the game date is not today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const gameDate = new Date(date);
          gameDate.setHours(0, 0, 0, 0);

          // If game is in the future and not today
          if (gameDate > today) {
            const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
            return dayOfWeek;
          }
          // If we found a valid date but it's today, return null
          return null;
        }
      } catch (e) {
        // Try next source
      }
    }

    return null;
  };

  const timeDisplay = formatTime(gameTime, gameStatus);
  const dayOfWeek = getDayOfWeek(gameTime, gameStatus);
  const isLive = gameStatus === 'Live';
  const isFinished = isGameFinished(gameStatus);

  // Show loading state
  if (!data && isLoading) {
    return (
      <div className="card game-card">
        <div className="card-body">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Always show the game card structure for football
  return (
    <div className={`card game-card ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="card-body">
        {/* Game Header - Modified DataTable structure */}
        <div 
          className="game-header-container" 
          style={{ 
            position: 'relative',
            cursor: isMobile ? 'pointer' : 'default',
            userSelect: 'none'
          }}
          onClick={isMobile ? () => onCollapseToggle && onCollapseToggle(!isCollapsed) : undefined}
        >
          {/* Game time - left justified when expanded, top left when collapsed on mobile */}
          {isMobile && isCollapsed ? (
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#6c757d',
                fontWeight: '500'
              }}>
                {timeDisplay}
              </div>
              {dayOfWeek && (
                <div style={{
                  fontSize: '0.6rem',
                  color: '#6c757d',
                  fontWeight: '400',
                  marginTop: '2px'
                }}>
                  {dayOfWeek}
                </div>
              )}
            </div>
          ) : (
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}>
              <div style={{
                fontSize: '0.9rem',
                color: isLive ? '#dc3545' : '#6c757d',
                fontWeight: '500'
              }}>
                {timeDisplay}
              </div>
              {dayOfWeek && (
                <div style={{
                  fontSize: '0.8rem',
                  color: '#6c757d',
                  fontWeight: '400',
                  marginTop: '2px'
                }}>
                  {dayOfWeek}
                </div>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            {/* Team logos and @ symbol - EXACT DataTable layout */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {(gameStatus !== 'Scheduled' && safeGame.awayScore !== null && safeGame.awayScore !== undefined) && (
                <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginRight: '8px' }}>
                  {safeGame.awayScore}
                </span>
              )}
              <img
                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                alt={safeGame.away}
                style={{ width: '32px', height: '32px', marginRight: '8px' }}
              />
              @
              <img
                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                alt={safeGame.home}
                style={{ width: '32px', height: '32px', margin: '0 8px' }}
              />
              {(gameStatus !== 'Scheduled' && safeGame.homeScore !== null && safeGame.homeScore !== undefined) && (
                <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginLeft: '8px' }}>
                  {safeGame.homeScore}
                </span>
              )}
              {/* Collapse indicator for mobile */}
              {isMobile && (
                <span style={{ 
                  marginLeft: '12px', 
                  fontSize: '1.2rem',
                  color: '#6c757d'
                }}>
                  {isCollapsed ? '▼' : '▲'}
                </span>
              )}
            </div>
            
            {/* Full team names for football - RIGHT BELOW LOGOS */}
            <div style={{ 
              fontSize: isMobile ? '0.9rem' : '0.75rem', 
              fontWeight: isMobile ? 'bold' : 'normal',
              color: '#6c757d', 
              textAlign: 'center'
            }}>
              {safeGame.awayTeamFull} vs {safeGame.homeTeamFull}
            </div>
          </div>
        </div>

        {/* Mobile book navigation arrows */}
        {isMobile && totalPages > 1 && (
          <div className="mobile-book-nav">
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={handlePrevBook}
              disabled={totalPages <= 1}
            >
              ←
            </button>
            <span className="mx-2">
              {mobileBookIndex + 1} / {totalPages}
            </span>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={handleNextBook}
              disabled={totalPages <= 1}
            >
              →
            </button>
          </div>
        )}

        {/* Market selector */}
        {!isCollapsed && availableMarkets && availableMarkets.length > 0 && (
          <div className="market-selector mb-3">
            <select 
              className="form-select form-select-sm"
              value={selectedMarket}
              onChange={(e) => onMarketSelection(safeGame.gameIDmlb, e.target.value)}
            >
              {availableMarkets
                .filter(market => market.isMainline) // Only show mainline markets for football
                .map(market => (
                  <option key={market.key} value={market.key}>
                    {market.name}
                  </option>
                ))
              }
            </select>
          </div>
        )}

        {/* Content based on data availability */}
        {!isCollapsed && (
          <div className="odds-content">
            {!data || data.length === 0 ? (
              <div className="no-odds-message">
                <p>No odds available for this game</p>
              </div>
            ) : (
              <div className="odds-grid">
                {/* Debug: Check the data structure */}
                {console.log('FootballDataTable data for', safeGame.sport, ':', data)}
                {console.log('Selected market:', selectedMarket)}
                
                {/* For NCAAF, use the same components as NFL for consistency */}
                {(() => {
                  // Check if the data is already structured for the table components
                  // For h2h, check if we have team data with h2h_ prefixed fields
                  const hasH2hData = data && Array.isArray(data) && data.length > 0 &&
                                     (data[0].teamId || data[0].hasOwnProperty('h2h_draftkings') ||
                                      data[0].hasOwnProperty('h2h_fanduel'));
                  const hasStructuredData = hasH2hData ||
                                           (data && Array.isArray(data) && data.length > 0 &&
                                            (data[0].playerId?.startsWith('totals_') ||
                                             data[0].playerId?.startsWith('spreads_')));

                  if (selectedMarket === 'h2h' && hasH2hData) {
                    // Filter h2h data
                    const h2hData = data.filter(player =>
                      player.teamId === 'away' || player.teamId === 'home'
                    );

                    // Sort h2h data (away first, then home)
                    h2hData.sort((a, b) => {
                      if (a.team === 'away' && b.team === 'home') return -1;
                      if (a.team === 'home' && b.team === 'away') return 1;
                      return 0;
                    });

                    return (
                      <H2hTable
                        h2hData={h2hData}
                        bookLogos={currentBooks}
                        getBookLogoUrl={getBookLogoUrl}
                        getCellStyle={(book, odds, allOdds) => {
                          // Basic tier styling
                          if (!odds || !allOdds) return {};
                          const validOdds = allOdds.filter(o => o !== null && o !== undefined);
                          if (validOdds.length === 0) return {};

                          const maxOdds = Math.max(...validOdds);
                          const isMax = odds === maxOdds;

                          if (isMax && validOdds.length > 1) {
                            const secondMax = Math.max(...validOdds.filter(o => o !== maxOdds));
                            const diff = Math.abs(maxOdds - secondMax);

                            if (diff >= 25) {
                              return { backgroundColor: '#28a745', color: 'yellow', fontWeight: 'bold' };
                            } else if (diff >= 15) {
                              return { backgroundColor: '#ffc107', color: '#000', fontWeight: 'bold' };
                            } else if (diff >= 5) {
                              return { backgroundColor: '#17a2b8', color: 'white', fontWeight: 'bold' };
                            }
                          }
                          return {};
                        }}
                        getTooltipText={() => ''}
                        onOddsClick={onOddsClick}
                        game={safeGame}
                        isMobile={isMobile}
                        formatOdds={(odds) => {
                          if (odds === null || odds === undefined) return '-';
                          return odds > 0 ? `+${odds}` : `${odds}`;
                        }}
                      />
                    );
                  } else if (selectedMarket === 'totals' && hasStructuredData) {
                    const totalsData = data.filter(player =>
                      player.playerId?.startsWith('totals_')
                    );

                    return (
                      <TotalsTable
                        data={totalsData}
                        game={safeGame}
                        bookmakers={currentBooks}
                        getBookLogoUrl={getBookLogoUrl}
                        getTeamLogoUrl={getTeamLogoUrl}
                        isMobile={isMobile}
                        onOddsClick={onOddsClick}
                      />
                    );
                  } else if (selectedMarket === 'spreads' && hasStructuredData) {
                    const spreadsData = data.filter(player =>
                      player.playerId?.startsWith('spreads_')
                    );

                    return (
                      <SpreadsTable
                        data={spreadsData}
                        game={safeGame}
                        bookmakers={currentBooks}
                        getBookLogoUrl={getBookLogoUrl}
                        getTeamLogoUrl={getTeamLogoUrl}
                        isMobile={isMobile}
                        onOddsClick={onOddsClick}
                      />
                    );
                  }

                  // Fallback to old handling for data that's not yet structured properly
                  const hasMarkets = data && data[0]?.markets;
                  const isTeamMarket = selectedMarket === 'h2h' || selectedMarket === 'spreads' || selectedMarket === 'totals';

                  if (hasMarkets && isTeamMarket) {
                    // Handle team markets (h2h, spreads, totals)
                    const marketData = data[0].markets[0];
                    const books = marketData?.books || {};

                    // For NFL h2h, books contain team names as keys
                    // For NCAAF, it might be structured differently
                    // Let's handle both cases

                    // Check if this is NFL-style (team names as keys in each book)
                    const firstBook = Object.values(books)[0];
                    const isNFLStyle = firstBook && typeof firstBook === 'object' && !firstBook.odds;
                    
                    if (isNFLStyle) {
                      // NFL style: books.draftkings = { "dallas cowboys": 320, "philadelphia eagles": -410 }
                      const teamNames = Object.keys(firstBook);
                      const awayTeamKey = teamNames.find(name => 
                        name.toLowerCase().includes(safeGame.away.toLowerCase()) ||
                        name.toLowerCase().includes(safeGame.awayTeamFull.toLowerCase())
                      ) || teamNames[0];
                      const homeTeamKey = teamNames.find(name => 
                        name.toLowerCase().includes(safeGame.home.toLowerCase()) ||
                        name.toLowerCase().includes(safeGame.homeTeamFull.toLowerCase())
                      ) || teamNames[1];
                      
                      return (
                        <div className="table-responsive">
                          <table className="table table-sm table-dark">
                            <thead>
                              <tr>
                                <th style={{ width: '150px' }}>Team</th>
                                {currentBooks.map(book => (
                                  <th key={book} className="text-center" style={{ minWidth: '80px' }}>
                                    <img
                                      src={getBookLogoUrl(book)}
                                      alt={book}
                                      style={{ height: '20px', width: 'auto' }}
                                    />
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {/* Away Team Row */}
                              <tr>
                                <td className="fw-bold">
                                  <img
                                    src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                    alt={safeGame.away}
                                    style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                  />
                                  {safeGame.away}
                                </td>
                                {currentBooks.map(book => {
                                  const bookData = books[book];
                                  const odds = bookData?.[awayTeamKey];
                                  
                                  return (
                                    <td key={book} className="text-center">
                                      {odds !== undefined ? (
                                        <button
                                          className="btn btn-sm btn-outline-success"
                                          onClick={() => onOddsClick && onOddsClick({ odds, book, team: safeGame.away })}
                                          style={{ fontSize: '12px', padding: '2px 6px' }}
                                        >
                                          {odds > 0 ? `+${odds}` : odds}
                                        </button>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                              
                              {/* Home Team Row */}
                              <tr>
                                <td className="fw-bold">
                                  <img
                                    src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                    alt={safeGame.home}
                                    style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                  />
                                  {safeGame.home}
                                </td>
                                {currentBooks.map(book => {
                                  const bookData = books[book];
                                  const odds = bookData?.[homeTeamKey];
                                  
                                  return (
                                    <td key={book} className="text-center">
                                      {odds !== undefined ? (
                                        <button
                                          className="btn btn-sm btn-outline-success"
                                          onClick={() => onOddsClick && onOddsClick({ odds, book, team: safeGame.home })}
                                          style={{ fontSize: '12px', padding: '2px 6px' }}
                                        >
                                          {odds > 0 ? `+${odds}` : odds}
                                        </button>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    } else {
                      // NCAAF style or other format - need to check console logs to see structure
                      return (
                        <div className="text-center text-muted">
                          <small>Check console for data structure - format not recognized</small>
                        </div>
                      );
                    }
                  } else if (data && data.length > 0 && data[0].player) {
                    // Player props (ATDS, 1st TD, Last TD)
                    return (
                      <div className="table-responsive">
                        <table className="table table-sm table-dark">
                          <thead>
                            <tr>
                              <th style={{ width: '200px' }}>Player</th>
                              {currentBooks.map(book => (
                                <th key={book} className="text-center" style={{ minWidth: '80px' }}>
                                  <img
                                    src={getBookLogoUrl(book)}
                                    alt={book}
                                    style={{ height: '20px', width: 'auto' }}
                                  />
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data.map(player => (
                              <tr key={player.player}>
                                <td className="fw-bold">
                                  {player.player}
                                  {player.team && (
                                    <span className="text-muted ms-2" style={{ fontSize: '11px' }}>
                                      ({player.team})
                                    </span>
                                  )}
                                </td>
                                {currentBooks.map(book => {
                                  const bookOdds = player.books?.[book];
                                  
                                  return (
                                    <td key={book} className="text-center">
                                      {bookOdds ? (
                                        <button
                                          className="btn btn-sm btn-outline-success"
                                          onClick={() => onOddsClick && onOddsClick({ ...bookOdds, book, player: player.player })}
                                          style={{ fontSize: '12px', padding: '2px 6px' }}
                                        >
                                          {bookOdds.odds > 0 ? `+${bookOdds.odds}` : bookOdds.odds}
                                        </button>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    // Unknown format
                    return (
                      <div className="text-center text-muted">
                        <small>No odds data available</small>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        )}

        {/* Last update timestamp */}
        {lastUpdate && !isCollapsed && (
          <div className="text-center mt-2">
            <small className="text-muted">
              Last updated: {new Date(lastUpdate).toLocaleTimeString()}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default FootballDataTable;